import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from '@components/DataTable';

/**
 * The persistence rules for a DataGrid's column layout, as pure functions.
 *
 * Two tables share these — the Library page's media table and the Libraries page's table —
 * via `useGridColumnPreferences`. They live here rather than in the hook so the parts that are
 * genuinely tricky (visibility-model normalisation, the write-loop guard, forward
 * compatibility with columns added after a user last reordered) are testable without React or a
 * grid: see `__tests__/gridColumnPreferences.test.ts`.
 *
 * Only *deviations from the defaults* are ever stored. That keeps the persisted payload small and
 * means a future change to a table's default order or default visibility still reaches every user
 * who never customised it.
 *
 * Types are `import type` only, so the test can run under `npx tsx` without loading the React table.
 */

export type ColumnVisibility = Record<string, boolean>;

export interface ColumnSchema {
  /** Every field the table can show, in the order it ships with. */
  defaultOrder: readonly string[];
  /**
   * Defaults for the *togglable* columns. Its KEY SET is the definition of "togglable":
   * a field absent from here (the anchor column, an actions kebab, the selection column `__check__`) is
   * never persisted, and a stored value for it is ignored on read.
   */
  defaultVisibility: Readonly<ColumnVisibility>;
  /**
   * Field held at index 0 regardless of what is stored — the row's identity column. Optional;
   * omit for a table where every column is freely orderable.
   */
  anchorField?: string;
  /** Non-reorderable identity/actions columns kept at the edges, including saved layouts. */
  leadingFields?: readonly string[];
  trailingFields?: readonly string[];
}

/**
 * Folds data-driven visibility overrides into the static defaults.
 *
 * The media table uses this for columns that should default to visible only when the data has
 * anything to put in them (`publishedAt`, `youtubeId`). Overrides outside the togglable set are
 * dropped: they would create a key the diff writer can never clear.
 */
export function resolveDefaultVisibility(
  defaultVisibility: Readonly<ColumnVisibility>,
  overrides?: Readonly<ColumnVisibility>,
): ColumnVisibility {
  const result: ColumnVisibility = { ...defaultVisibility };
  if (!overrides) return result;
  for (const [field, value] of Object.entries(overrides)) {
    if (field in defaultVisibility) result[field] = value;
  }
  return result;
}

/** Applies the stored diff on top of the (possibly data-driven) defaults. */
export function resolveVisibilityModel(
  stored: Readonly<ColumnVisibility>,
  dynamicDefaults: Readonly<ColumnVisibility>,
): GridColumnVisibilityModel {
  const model: GridColumnVisibilityModel = { ...dynamicDefaults };
  for (const [field, value] of Object.entries(stored)) {
    // Ignore anything outside the togglable set, so a hand-edited settings document can't hide
    // a column the UI gives no way to restore.
    if (field in dynamicDefaults) model[field] = value;
  }
  return model;
}

/**
 * The diff to persist after the user shows or hides a column.
 *
 * Visibility models may only include columns explicitly set to `false` — a visible
 * column is represented by *absence*, not by `true`. Hence `model[field] !== false` rather than a
 * plain lookup.
 */
export function nextStoredVisibility(
  model: GridColumnVisibilityModel,
  dynamicDefaults: Readonly<ColumnVisibility>,
): ColumnVisibility {
  const diff: ColumnVisibility = {};
  for (const field of Object.keys(dynamicDefaults)) {
    const isVisible = model[field] !== false;
    if (dynamicDefaults[field] !== isVisible) diff[field] = isVisible;
  }
  return diff;
}

/**
 * The order to actually render, given what's stored.
 *
 * Returns `defaultOrder` **by reference** when nothing is stored. That identity matters: it is
 * what lets the hook's `columns` memo stay stable for users who have never reordered, and MUI
 * rebuilds its column state whenever that array's identity changes.
 */
export function resolveColumnOrder(
  storedOrder: readonly string[],
  {
    defaultOrder,
    anchorField,
    leadingFields = [],
    trailingFields = [],
  }: Pick<ColumnSchema, 'defaultOrder' | 'anchorField' | 'leadingFields' | 'trailingFields'>,
): readonly string[] {
  if (storedOrder.length === 0) return defaultOrder;

  const known = new Set(defaultOrder);
  const seen = new Set<string>();
  // The anchor is prepended at the end of this function, so skip it while walking.
  if (anchorField) seen.add(anchorField);

  const result: string[] = [];
  // Honour the stored order, dropping fields that no longer exist in the schema (a column we've
  // since removed) and de-duping a corrupt document.
  for (const field of storedOrder) {
    if (known.has(field) && !seen.has(field)) {
      result.push(field);
      seen.add(field);
    }
  }
  // Columns added since the user last reordered go at the end rather than vanishing.
  for (const field of defaultOrder) {
    if (!seen.has(field)) {
      result.push(field);
      seen.add(field);
    }
  }

  const leading = [...new Set([...leadingFields, ...(anchorField ? [anchorField] : [])])].filter(
    (field) => known.has(field),
  );
  const trailing = [...new Set(trailingFields)].filter(
    (field) => known.has(field) && !leading.includes(field),
  );
  return [
    ...leading,
    ...result.filter((field) => !leading.includes(field) && !trailing.includes(field)),
    ...trailing,
  ];
}

/**
 * What to store after a reorder, or `undefined` for "don't write".
 *
 * Callers must test `=== undefined`: `[]` is a real write, meaning "back to defaults".
 *
 * Two guards, both load-bearing:
 * - **Equal to the effective order** → skip redundant preference writes.
 * - **Equal to the defaults** → store `[]`, so the preference returns to "use defaults" and a
 *   future change to `defaultOrder` still reaches this user.
 */
export function nextStoredOrder({
  gridOrder,
  effectiveOrder,
  defaultOrder,
}: {
  gridOrder: readonly string[];
  effectiveOrder: readonly string[];
  defaultOrder: readonly string[];
}): string[] | undefined {
  // Synthetic columns (such as the selection checkbox) are not persisted.
  const order = gridOrder.filter((field) => defaultOrder.includes(field));
  if (isSameOrder(order, effectiveOrder)) return undefined;
  if (isSameOrder(order, defaultOrder)) return [];
  return order;
}

export function isSameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((field, index) => field === b[index]);
}

/**
 * Reorders the column defs to match `order`.
 *
 * The definition order is the table’s controlled, persisted column order.
 */
export function orderColumnDefs<R extends GridValidRowModel>(
  columns: readonly GridColDef<R>[],
  order: readonly string[],
): GridColDef<R>[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const result: GridColDef<R>[] = [];
  for (const field of order) {
    const column = byField.get(field);
    if (column) result.push(column);
  }
  // Defensive: a def added without being registered in `defaultOrder` keeps its place at the end
  // rather than disappearing from the table.
  for (const column of columns) {
    if (!order.includes(column.field)) result.push(column);
  }
  return result;
}
